import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getBrief } from '../lifeos/api.js';
import { dayLabel, humanize, shortDate, to12h } from '../lifeos/format.js';
import SafeMarkdown from '../lifeos/SafeMarkdown.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Group, Loader, MoreRow, Page, Row } from '../ui/index.js';
import type { BriefSection } from '../lifeos/types.js';

/** Where the rest of a cut-short section lives, by section key. */
export const SECTION_HREF: Readonly<Record<string, { to: string; where: string }>> = {
  waiting: { to: '/inbox?status=needs_reply', where: 'Inbox' },
  unblocked: { to: '/inbox', where: 'Inbox' },
  due: { to: '/library?days=7', where: 'this week' },
  approvals: { to: '/agents', where: 'Agents' },
  to_sort: { to: '/tasks?view=to_sort', where: 'To sort' },
  focus: { to: '/tasks', where: 'Tasks' },
  done: { to: '/tasks?view=done_today', where: 'Done today' },
  agenda: { to: '/plan', where: 'the calendar' },
  clients: { to: '/people', where: 'People' },
};

const LEGACY_MORE = /^\\?\+(\d+) more$/;

/**
 * A section's items and how many were left out. `more` is the contract; a
 * brief stored before it existed ends its list with a '+N more' item, which
 * becomes the same count rather than a bullet that leads nowhere.
 */
export function sectionParts(s: BriefSection): { items: string[]; more: number } {
  let items = s.items;
  let more = s.more ?? 0;
  const last = items[items.length - 1];
  const legacy = last !== undefined ? LEGACY_MORE.exec(last.trim()) : null;
  if (legacy) {
    items = items.slice(0, -1);
    if (!s.more) more = Number(legacy[1]);
  }
  if (s.key === 'agenda') items = items.map(agendaTime);
  return { items, more };
}

/** An agenda line's leading 24h time in the 12h form Home uses ('14:00 Call' → '2:00 PM Call'). */
export function agendaTime(item: string): string {
  return item.replace(/^(\d{1,2}:\d{2})(?=\s)(?!\s*[AaPp][Mm]\b)/, (t) => to12h(t));
}

/** The date subtitle, unless the title already names the day. */
export function briefSub(title: string, forDate: string): string | undefined {
  return title.includes(shortDate(forDate)) ? undefined : dayLabel(forDate);
}

/** Items a section shows before "Show N more". */
export const BRIEF_SHOWN = 3;

/**
 * A stored brief (`/brief/:id`) — the link at the bottom of every Signal
 * brief. One group per section; each shows three items and folds the rest,
 * and a section cut short upstream links to the page that holds the rest.
 * Item text is markdown from the composer, rendered safely; a brief without
 * sections falls back to its `bodyMd`.
 */
export default function Brief(): JSX.Element {
  const { id = '' } = useParams();
  const brief = useLoad(() => getBrief(id), [id]);
  const back = { to: '/', label: 'Today' };
  if (!brief.data) {
    return (
      <Page title="" back={back}>
        <Loader state={brief}>{() => null}</Loader>
      </Page>
    );
  }
  const b = brief.data;
  const title = b.title ?? `${humanize(b.kind)} brief`;
  return (
    <Page title={title} sub={briefSub(title, b.forDate)} back={back} className="la-brief">
      {b.sections.length > 0 ? (
        b.sections.map((s) => <Section key={s.key} s={s} />)
      ) : (
        <article className="ui-card la-md lo-md">
          <SafeMarkdown>{b.bodyMd}</SafeMarkdown>
        </article>
      )}
    </Page>
  );
}

function Section({ s }: { s: BriefSection }): JSX.Element {
  const { items, more } = sectionParts(s);
  const href = SECTION_HREF[s.key];
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, BRIEF_SHOWN);
  return (
    <Group header={s.title} label={s.title} className="la-brief__section">
      {shown.map((item, i) => (
        <Row key={i} className="la-brief__item" title={<SafeMarkdown inline>{item}</SafeMarkdown>} />
      ))}
      {items.length > shown.length && <MoreRow count={items.length - shown.length} onClick={() => setAll(true)} />}
      {more > 0 &&
        (href ? (
          <Row key="rest" className="la-brief__rest" to={href.to} tone="accent" title={`${more} more in ${href.where}`} />
        ) : (
          <Row key="rest" className="la-brief__rest" tone="muted" title={`${more} more`} />
        ))}
      {items.length === 0 && more === 0 && <Row tone="muted" title="Nothing here." />}
    </Group>
  );
}
